'use client';

import type { DefaultValues, FieldValues } from 'react-hook-form';
import type { ZodType } from 'zod';

import RichText from '@/components/ui/RichText/RichText';
import { getFormDefinition } from '@/lib/forms/formDefinitions';
import type { FieldConfig } from '@/types/forms';

import { SmartFormInline } from './SmartFormInline';
import { SmartFormLayout } from './SmartFormLayout';
import { SmartFormSplitLayout } from './SmartFormSplitLayout';
import { SmartFormModalController } from './SmartFormModalController';
import type { SmartFormBlockProps, SecurityConfig } from './types';
import { StyleOverrides } from '@/types/richTextTypes';

function FormContent<T extends FieldValues>({
  formId,
  schema,
  fields,
  defaultValues,
  submitLabel,
  columns,
  security,
  metadata,
  buildMetadata,
  recipientContext,
  renderFooterExtra,
  buttonContainerClassName,
  buttonClassName,
  buttonPaddingClassName,
  disableSubmitUntilFilled,
  anchorId,
}: {
  formId: string;
  schema: ZodType<T>;
  fields: FieldConfig[];
  defaultValues: DefaultValues<T>;
  submitLabel?: string;
  columns?: 1 | 2;
  security?: SecurityConfig;
  metadata?: Record<string, unknown>;
  buildMetadata?: () => Record<string, unknown>;
  recipientContext?: Record<string, unknown>;
  renderFooterExtra?: (values: T) => React.ReactNode;
  buttonContainerClassName?: string;
  buttonClassName?: string;
  buttonPaddingClassName?: string;
  disableSubmitUntilFilled?: boolean;
  anchorId?: string;
}) {
  return (
    <div id={anchorId}>
      <SmartFormInline<T>
        formId={formId}
        schema={schema}
        fields={fields}
        defaultValues={defaultValues}
        submitLabel={submitLabel}
        columns={columns}
        security={security}
        metadata={metadata}
        buildMetadata={buildMetadata}
        recipientContext={recipientContext}
        renderFooterExtra={renderFooterExtra}
        buttonContainerClassName={buttonContainerClassName}
        buttonClassName={buttonClassName}
        buttonPaddingClassName={buttonPaddingClassName}
        disableSubmitUntilFilled={disableSubmitUntilFilled}
      />
    </div>
  );
}

function HeadingDescription({
  heading,
  description,
}: {
  heading?: { content: string; styles?: StyleOverrides };
  description?: { content: string; styles?: StyleOverrides };
}) {
  return (
    <>
      {heading?.content && (
        <div className="mb-4">
          <RichText styles={heading.styles}>{heading.content}</RichText>
        </div>
      )}
      {description?.content && (
        <div className="mb-6">
          <RichText styles={description.styles}>{description.content}</RichText>
        </div>
      )}
    </>
  );
}

export function SmartFormBlock<T extends FieldValues>({
  formId,
  schema,
  fields,
  defaultValues,
  mode = 'inline',
  template = 'default',
  heading,
  description,
  submitLabel,
  columns,
  security,
  metadata,
  buildMetadata,
  recipientContext,
  renderFooterExtra,
  buttonContainerClassName,
  buttonClassName,
  buttonPaddingClassName,
  disableSubmitUntilFilled,
  triggerHash,
  anchorId,
  modal,
  textPosition,
  splitClassName,
  className,
}: SmartFormBlockProps<T>) {
  let resolvedSchema = schema;
  let resolvedFields = fields;
  let resolvedDefaultValues = defaultValues;

  if (formId && !resolvedSchema) {
    const definition = getFormDefinition(formId);
    if (definition) {
      resolvedSchema = definition.schema as unknown as typeof resolvedSchema;
      resolvedFields = definition.fields as typeof resolvedFields;
      resolvedDefaultValues =
        definition.defaultValues as typeof resolvedDefaultValues;
    }
  }

  if (!resolvedSchema || !resolvedFields || !resolvedDefaultValues) {
    return null;
  }

  const sharedProps: SmartFormBlockProps<T> = {
    formId,
    schema: resolvedSchema,
    fields: resolvedFields,
    defaultValues: resolvedDefaultValues,
    submitLabel,
    columns,
    security,
    metadata,
    buildMetadata,
    recipientContext,
    renderFooterExtra,
    buttonContainerClassName,
    buttonClassName,
    buttonPaddingClassName,
    disableSubmitUntilFilled,
    anchorId,
    heading,
    description,
    template,
  };

  const inlineForm = (
    <FormContent<T>
      {...sharedProps}
      formId={formId ?? 'unknown'}
      schema={resolvedSchema}
      fields={resolvedFields}
      defaultValues={resolvedDefaultValues}
      renderFooterExtra={renderFooterExtra}
      buttonContainerClassName={buttonContainerClassName}
      buttonClassName={buttonClassName}
      buttonPaddingClassName={buttonPaddingClassName}
      disableSubmitUntilFilled={disableSubmitUntilFilled}
    />
  );

  const headingDescription = (
    <HeadingDescription heading={heading} description={description} />
  );

  const wrappedContent =
    template === 'split' ? (
      <SmartFormSplitLayout
        heading={heading}
        description={description}
        form={inlineForm}
        textPosition={textPosition}
        className={splitClassName}
      />
    ) : (
      <SmartFormLayout template={template} className={className}>
        {headingDescription}
        {inlineForm}
      </SmartFormLayout>
    );

  if (mode === 'modal') {
    return (
      <SmartFormModalController<T>
        formId={formId ?? 'unknown'}
        schema={resolvedSchema}
        fields={resolvedFields}
        defaultValues={resolvedDefaultValues}
        title={heading?.content}
        description={description?.content}
        submitLabel={submitLabel}
        columns={columns}
        security={security}
        metadata={metadata}
        buildMetadata={buildMetadata}
        recipientContext={recipientContext}
        triggerHash={triggerHash}
        anchorId={anchorId}
        modal={modal}
      />
    );
  }

  if (mode === 'hybrid') {
    return (
      <>
        {wrappedContent}
        <SmartFormModalController<T>
          formId={formId ?? 'unknown'}
          schema={resolvedSchema}
          fields={resolvedFields}
          defaultValues={resolvedDefaultValues}
          title={heading?.content}
          description={description?.content}
          submitLabel={submitLabel}
          columns={columns}
          security={security}
          metadata={metadata}
          buildMetadata={buildMetadata}
          recipientContext={recipientContext}
          triggerHash={triggerHash}
          anchorId={anchorId}
          modal={modal}
        />
      </>
    );
  }

  return wrappedContent;
}
